import * as enInLocaleExports from './en-IN';
import { enInContent } from './en-IN';
import { defaultLocale, resolveLocale, supportedLocaleCodes, type LocaleCode } from './locales';
import {
  contentRequiredPageIds,
  ctaRequiredPageIds,
  pageRegistry,
  seoRequiredPageIds,
  type ContentRequiredPageId,
  type CtaRequiredPageId,
  type MarketingPageId,
  type SeoRequiredPageId,
} from './page-registry';
import type {
  ContentBundle,
  CTAContent,
  FAQContent,
  PageContent,
  PageSeoContent,
  SiteContent,
  TestimonialContent,
} from './schema';
import * as taLocaleExports from './ta';
import { taContent } from './ta';

const contentBundles = {
  'en-IN': enInContent,
  ta: taContent,
} satisfies Record<LocaleCode, ContentBundle>;

const localeExports = {
  'en-IN': enInLocaleExports,
  ta: taLocaleExports,
} as const;

function getBundle(locale?: LocaleCode | string | null): ContentBundle {
  return contentBundles[resolveLocale(locale)];
}

function getLocaleExports(locale?: LocaleCode | string | null) {
  return localeExports[resolveLocale(locale)];
}

function assertRequiredContent(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

export function getContent(locale?: LocaleCode | string | null): SiteContent {
  return getBundle(locale).site;
}

export function getNavigation(locale?: LocaleCode | string | null) {
  return getBundle(locale).navigation;
}

export function getCompany(locale?: LocaleCode | string | null) {
  return getBundle(locale).company;
}

export function getHomeCollections(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).homeCollections;
}

export function getGiftingFeatures(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).giftingFeatures;
}

export function getGiftingOptions(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).giftingOptions;
}

export function getValues(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).values;
}

export function getPromises(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).promises;
}

export function getTimeline(locale?: LocaleCode | string | null) {
  return getLocaleExports(locale).timeline;
}

export function getPageSeo(pageId: SeoRequiredPageId, locale?: LocaleCode | string | null): PageSeoContent {
  const seo = getBundle(locale).seo[pageId];
  assertRequiredContent(seo, `Missing required SEO content for page: ${pageId}`);
  return seo;
}

export function getPageCta(pageId: CtaRequiredPageId, locale?: LocaleCode | string | null): CTAContent {
  const cta = getBundle(locale).ctas[pageId];
  assertRequiredContent(cta, `Missing required CTA content for page: ${pageId}`);
  return cta;
}

export function getDefaultGlobalCta(locale?: LocaleCode | string | null): CTAContent {
  return getBundle(locale).defaultGlobalCta;
}

export function getPageContent(pageId: ContentRequiredPageId, locale?: LocaleCode | string | null): PageContent {
  const pageContent = getBundle(locale).pages[pageId];
  assertRequiredContent(pageContent, `Missing required page content for page: ${pageId}`);
  return pageContent;
}

export function getOptionalPageCta(pageId: MarketingPageId, locale?: LocaleCode | string | null): CTAContent | null {
  const ctas = getBundle(locale).ctas as Partial<Record<MarketingPageId, CTAContent>>;
  return ctas[pageId] ?? null;
}

export function getOptionalPageContent(
  pageId: MarketingPageId,
  locale?: LocaleCode | string | null
): PageContent | null {
  const pages = getBundle(locale).pages as Partial<Record<MarketingPageId, PageContent>>;
  return pages[pageId] ?? null;
}

export function getTestimonials(filters: { page?: MarketingPageId; locale?: LocaleCode | string | null } = {}) {
  const entries = getBundle(filters.locale).testimonials;

  if (!filters.page) {
    return [...entries] satisfies TestimonialContent[];
  }

  return entries.filter((testimonial) => testimonial.page === filters.page);
}

export function getFaqs(filters: { page?: MarketingPageId; locale?: LocaleCode | string | null } = {}) {
  const entries = getBundle(filters.locale).faqs;

  if (!filters.page) {
    return [...entries] satisfies FAQContent[];
  }

  return entries.filter((faq) => faq.pages.includes(filters.page as MarketingPageId));
}

export function validateContent(locale: LocaleCode = defaultLocale) {
  const bundle = getBundle(locale);
  const errors: string[] = [];

  seoRequiredPageIds.forEach((pageId) => {
    const seo = bundle.seo[pageId];

    if (!seo) {
      errors.push(`Missing required SEO content for ${pageId}`);
    } else if (seo.canonicalPath !== pageRegistry[pageId].path) {
      errors.push(
        `SEO canonical path for ${pageId} must be ${pageRegistry[pageId].path}, received ${seo.canonicalPath}`
      );
    }
  });

  if (bundle.locale !== locale) {
    errors.push(`Bundle locale mismatch for ${locale}: ${bundle.locale}`);
  }

  if (bundle.site.locale !== locale) {
    errors.push(`Site locale mismatch for ${locale}: ${bundle.site.locale}`);
  }

  ctaRequiredPageIds.forEach((pageId) => {
    if (!bundle.ctas[pageId]) {
      errors.push(`Missing required CTA content for ${pageId}`);
    }
  });

  contentRequiredPageIds.forEach((pageId) => {
    if (!bundle.pages[pageId]) {
      errors.push(`Missing required page content for ${pageId}`);
    }
  });

  const checkUnique = (label: string, ids: readonly string[]) => {
    const seen = new Set<string>();
    ids.forEach((id) => {
      if (seen.has(id)) {
        errors.push(`Duplicate ${label} id: ${id}`);
      }
      seen.add(id);
    });
  };

  checkUnique(
    'testimonial',
    bundle.testimonials.map((testimonial) => testimonial.id)
  );
  checkUnique(
    'faq',
    bundle.faqs.map((faq) => faq.id)
  );
  checkUnique(
    'primary navigation',
    bundle.navigation.primary.map((item) => item.id)
  );
  checkUnique(
    'footer navigation',
    bundle.navigation.footer.map((item) => item.id)
  );
  checkUnique(
    'menu navigation',
    bundle.navigation.menu.map((item) => item.id)
  );
  checkUnique(
    'cta',
    [...Object.values(bundle.ctas), bundle.defaultGlobalCta].map((cta) => cta.id)
  );

  Object.values(bundle.pages).forEach((page) => {
    checkUnique(
      `${page.id} content section`,
      page.sections.map((section) => section.id)
    );
  });

  [...Object.values(bundle.ctas), bundle.defaultGlobalCta].forEach((cta) => {
    if (!isValidHref(cta.ctaUrl)) {
      errors.push(`Invalid CTA URL for ${cta.id}: ${cta.ctaUrl}`);
    }

    if (cta.ctaUrl.startsWith('/ta')) {
      errors.push(`CTA URL must store the base route, not a locale-prefixed route, for ${cta.id}: ${cta.ctaUrl}`);
    }
  });

  [...bundle.navigation.primary, ...bundle.navigation.footer, ...bundle.navigation.menu].forEach((item) => {
    if (item.href.startsWith('/ta')) {
      errors.push(
        `Navigation href must store the base route, not a locale-prefixed route, for ${item.id}: ${item.href}`
      );
    }
  });

  bundle.testimonials.forEach((testimonial) => {
    if (testimonial.rating < 0 || testimonial.rating > 5) {
      errors.push(`Invalid testimonial rating for ${testimonial.id}: ${testimonial.rating}`);
    }
  });

  if (errors.length) {
    throw new Error(`Static content validation failed:\n${errors.join('\n')}`);
  }
}

export function validateAllContent() {
  supportedLocaleCodes.forEach((locale) => validateContent(locale));

  const defaultBundle = getBundle(defaultLocale);
  supportedLocaleCodes
    .filter((locale) => locale !== defaultLocale)
    .forEach((locale) => {
      const bundle = getBundle(locale);
      assertMatchingIds(
        `primary navigation ids for ${locale}`,
        defaultBundle.navigation.primary.map((item) => item.id),
        bundle.navigation.primary.map((item) => item.id)
      );
      assertMatchingIds(
        `footer navigation ids for ${locale}`,
        defaultBundle.navigation.footer.map((item) => item.id),
        bundle.navigation.footer.map((item) => item.id)
      );
      assertMatchingIds(
        `menu navigation ids for ${locale}`,
        defaultBundle.navigation.menu.map((item) => item.id),
        bundle.navigation.menu.map((item) => item.id)
      );
    });
}

function assertMatchingIds(label: string, expected: readonly string[], actual: readonly string[]) {
  if (expected.join('|') !== actual.join('|')) {
    throw new Error(`Static content validation failed: ${label} must match default locale ids`);
  }
}

function isValidHref(href: string) {
  if (href.startsWith('/')) {
    return true;
  }

  try {
    new URL(href);
    return true;
  } catch {
    return false;
  }
}

if (import.meta.env.DEV) {
  validateAllContent();
}

export * from './en-IN';
export * from './locales';
export * from './page-registry';
export type * from './schema';
